import { useCallback, useMemo, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { formatQuantity } from '@/format/money'
import type { ColumnDescriptor, ListRow, SortSpec } from './types'
import { CellValue, displayText } from './cellValue'
import { columnLabel } from './columnLabel'
import { LIST_CARDS_QUERY, useMediaQuery } from '@/app/viewport'

/** Label/value pairs under a card's title: enough to tell rows apart, few enough to scan. */
const CARD_FIELDS = 4

/**
 * Универсальная таблица со серверной сортировкой и выделением (ТЗ §5.1).
 *
 * Сортировка и пагинация — **серверные**. Клиентская сортировка сортирует загруженную
 * страницу, то есть сто строк из миллиона: пользователь нажимает «по сумме», видит
 * упорядоченные сто строк и уверен, что смотрит на самые крупные сделки. Это хуже, чем
 * отсутствие сортировки, потому что выглядит правильно.
 *
 * Виртуализации нет намеренно: страница — сто строк, и они помещаются в DOM без затей.
 * Виртуализация нужна там, где в DOM пытаются положить весь набор, а здесь его туда
 * никогда не кладут — этим и занимается курсорная пагинация.
 */

interface DataTableProps {
  /** Ключ каталога: по нему переводятся коды значений (`value.<entity>.<column>.<код>`). */
  entity?: string
  columns: string[]
  descriptors: ColumnDescriptor[]
  rows: ListRow[]
  sort: SortSpec[]
  onToggleSort: (column: string) => void
  /** Выделенные строки; отсутствие обработчика убирает колонку с флажками. */
  selection?: Set<string>
  onSelectionChange?: (selection: Set<string>) => void
  isLoading?: boolean
  /**
   * Открыть карточку строки (#66).
   *
   * Отсутствие обработчика оставляет таблицу такой, какой она была: сущность без
   * описанной карточки открывать некуда, и делать строку кликабельной значило бы обещать
   * переход, который приведёт на «страница не найдена».
   */
  onOpenRow?: (id: string) => void
  /** Класс ячеек колонки (`mono` — сверяют глазами с этикеткой, `strong` — главная цифра). */
  columnClass?: Record<string, string>
  /** Своя отрисовка значения; `undefined` — обычная ячейка. */
  renderCell?: (row: ListRow, column: string) => ReactNode | undefined
  /**
   * Своё тело таблицы — для группировки: каждая группа читает свои строки сама и рисует их
   * переданной функцией, чтобы строка группы и строка плоского списка не разошлись.
   */
  renderBody?: (renderRow: (row: ListRow) => ReactNode, columnCount: number) => ReactNode
}

export function DataTable({
  entity,
  columns,
  descriptors,
  rows,
  sort,
  onToggleSort,
  selection,
  onSelectionChange,
  isLoading,
  onOpenRow,
  columnClass,
  renderCell,
  renderBody,
}: DataTableProps) {
  const { t, locale } = useApp()

  const sortable = useMemo(() => {
    const map = new Map<string, boolean>()
    descriptors.forEach((column) => map.set(column.name, column.sortable))
    return map
  }, [descriptors])
  const byName = useMemo(
    () => new Map(descriptors.map((column) => [column.name, column])),
    [descriptors],
  )

  const selectable = Boolean(onSelectionChange)
  const current = sort[0]

  const toggleRow = useCallback(
    (id: string) => {
      if (!onSelectionChange) return
      const next = new Set(selection ?? [])
      if (!next.delete(id)) next.add(id)
      onSelectionChange(next)
    },
    [onSelectionChange, selection],
  )

  const allOnPageSelected =
    rows.length > 0 && rows.every((row) => selection?.has(row.id))

  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return
    const next = new Set(selection ?? [])
    if (allOnPageSelected) {
      rows.forEach((row) => next.delete(row.id))
    } else {
      rows.forEach((row) => next.add(row.id))
    }
    onSelectionChange(next)
  }, [allOnPageSelected, onSelectionChange, rows, selection])

  const columnCount = columns.length + (selectable ? 1 : 0)
  const tdClass = (column: string) =>
    [byName.get(column)?.type === 'number' ? 'num' : '', columnClass?.[column] ?? '']
      .filter(Boolean)
      .join(' ') || undefined

  // Phone (#109): a table of six columns either squeezes every value into an ellipsis or
  // scrolls the page sideways. Rows become cards instead — the first column as the title,
  // the next few as label/value pairs. A grouped list keeps its table (the group rows are
  // table rows) and scrolls inside its frame.
  const asCards = useMediaQuery(LIST_CARDS_QUERY) && !renderBody
  if (asCards) {
    const [titleColumn, ...rest] = columns
    const fields = rest.slice(0, CARD_FIELDS)
    const sortableColumns = columns.filter((column) => sortable.get(column) !== false)
    return (
      <div className="row-cards-wrap">
        {sortableColumns.length > 0 && (
          <div className="row-cards-sort">
            <label className="t-sm muted" htmlFor="row-cards-sort">{t('list.cards.sortBy')}</label>
            <select
              id="row-cards-sort"
              value={current?.column ?? ''}
              onChange={(event) => {
                if (event.target.value) onToggleSort(event.target.value)
              }}
            >
              {!current && <option value="">—</option>}
              {sortableColumns.map((column) => (
                <option key={column} value={column}>{columnLabel(t, descriptors, column)}</option>
              ))}
            </select>
            {current && (
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-icon"
                aria-label={t(current.descending ? 'list.cards.sortDescending' : 'list.cards.sortAscending')}
                title={t(current.descending ? 'list.cards.sortDescending' : 'list.cards.sortAscending')}
                onClick={() => onToggleSort(current.column)}
              >
                <Icon name={current.descending ? 'arrow-down' : 'arrow-up'} size="sm" />
              </button>
            )}
          </div>
        )}
        <ul className="row-cards" aria-label={t('list.cards.label')}>
          {rows.map((row) => (
            <li key={row.id} className={`row-card${selection?.has(row.id) ? ' is-selected' : ''}`}>
              <div className="row-card-head">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={selection?.has(row.id) ?? false}
                    onChange={() => toggleRow(row.id)}
                    aria-label={t('list.selectRow')}
                  />
                )}
                <div className="row-card-title">
                  {titleColumn && (onOpenRow ? (
                    <button type="button" className="cell-open" onClick={() => onOpenRow(row.id)}>
                      {displayText(row, titleColumn, byName.get(titleColumn), entity, t, locale) || t('list.open')}
                    </button>
                  ) : (
                    renderCell?.(row, titleColumn) ?? (
                      <CellValue values={row} column={titleColumn} descriptor={byName.get(titleColumn)} entity={entity} />
                    )
                  ))}
                </div>
              </div>
              {fields.length > 0 && (
                <dl className="row-card-fields">
                  {fields.map((column) => (
                    <div key={column} style={{ display: 'contents' }}>
                      <dt>{columnLabel(t, descriptors, column)}</dt>
                      <dd className={columnClass?.[column]}>
                        {renderCell?.(row, column) ?? (
                          <CellValue values={row} column={column} descriptor={byName.get(column)} entity={entity} />
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
        {rows.length === 0 && !isLoading && <p className="row-cards-empty">{t('list.empty')}</p>}
      </div>
    )
  }

  const renderRow = (row: ListRow) => (
    <tr
      key={row.id}
      className={[
        selection?.has(row.id) ? 'is-selected' : '',
        onOpenRow ? 'is-openable' : '',
      ]
        .filter(Boolean)
        .join(' ') || undefined}
    >
      {selectable && (
        <td className="chk">
          <input
            type="checkbox"
            checked={selection?.has(row.id) ?? false}
            onChange={() => toggleRow(row.id)}
            aria-label={t('list.selectRow')}
          />
        </td>
      )}
      {columns.map((column, index) => (
        <td key={column} className={tdClass(column)}>
          {/* Переход — кнопка в первой ячейке, а не `onClick` на строке.
              Клик по строке отнял бы у неё выделение флажком и копирование
              текста, а с клавиатуры такая строка недостижима вовсе: обработчик
              на `<tr>` не даёт ни фокуса, ни Enter. */}
          {index === 0 && onOpenRow ? (
            <button
              type="button"
              className="cell-open"
              onClick={() => onOpenRow(row.id)}
            >
              {displayText(row, column, byName.get(column), entity, t, locale) ||
                t('list.open')}
            </button>
          ) : (
            (renderCell?.(row, column) ?? (
              <CellValue
                values={row}
                column={column}
                descriptor={byName.get(column)}
                entity={entity}
              />
            ))
          )}
        </td>
      ))}
    </tr>
  )

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {selectable && (
              <th className="chk">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  disabled={rows.length === 0}
                  onChange={toggleAll}
                  aria-label={t('list.selectPage')}
                />
              </th>
            )}
            {columns.map((column) => {
              const isSorted = current?.column === column
              const canSort = sortable.get(column) !== false
              return (
                <th
                  key={column}
                  aria-sort={ariaSort(isSorted, current)}
                  className={byName.get(column)?.type === 'number' ? 'num' : undefined}
                >
                  {canSort ? (
                    <button
                      type="button"
                      className={`sort${isSorted ? ' is-on' : ''}`}
                      onClick={() => onToggleSort(column)}
                    >
                      {columnLabel(t, descriptors, column)}
                      {isSorted && (
                        <Icon name={current.descending ? 'arrow-down' : 'arrow-up'} size="xs" />
                      )}
                    </button>
                  ) : (
                    columnLabel(t, descriptors, column)
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {renderBody ? renderBody(renderRow, columnCount) : rows.map(renderRow)}

          {!renderBody && rows.length === 0 && !isLoading && (
            <tr>
              <td colSpan={columnCount} className="muted">
                {t('list.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function ariaSort(isSorted: boolean, current?: SortSpec): 'ascending' | 'descending' | 'none' {
  if (!isSorted || !current) return 'none'
  return current.descending ? 'descending' : 'ascending'
}

/**
 * Приводит значение ячейки к тексту.
 *
 * Пустая ячейка вместо «null»: слово в таблице читается как значение, и его начинают
 * искать в справочниках. Даты — в локали пользователя, потому что ISO-строку в таблице
 * никто не читает.
 */
export function formatCell(value: unknown, locale: string): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? '✓' : '—'
  if (typeof value === 'number') {
    // A plain number is a quantity, not money: money columns are marked by the server and
    // formatted in `displayText` (#91). No padding zeros — 120 pieces are not "120,00".
    return formatQuantity(value, locale)
  }
  if (typeof value === 'string') {
    // Даты приходят строками ISO; распознаём по форме, а не по имени колонки — имя
    // задаёт каталог на сервере, и привязка к нему разъехалась бы при первой правке.
    const iso = /^\d{4}-\d{2}-\d{2}(T|$)/
    if (iso.test(value)) {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return value.length === 10
          ? parsed.toLocaleDateString(locale)
          : parsed.toLocaleString(locale)
      }
    }
    return value
  }
  return String(value)
}
